import { useMemo } from 'react';

import { Box, Button, Stack } from '@mui/material';
import { extractHostname } from '@realadvisor/agency-utils';
import { Globe, MapPin, Phone } from 'lucide-react';

import { getPhoneLabel } from '@modules/_shared/components/PhoneInput';
import { EditableChip } from '@modules/_shared/components/visibility-drawer/edit-modals/EditableChip';
import { YearsInBusinessIcon } from '@modules/_shared/components/visibility-drawer/edit-modals/YearsInBusinessIcon';
import {
  formatAddress,
  formatYearsInBusiness,
} from '@modules/_shared/utils/formatting';
import { type Translate, useLocale } from '@shared/hooks/locale';

type AgencyProfileBadgesProps = {
  address?: {
    street_number?: string | null;
    route?: string | null;
    postcode?: string | null;
    locality?: string | null;
    state?: string | null;
    country?: string | null;
  } | null;
  website?: string | null;
  activeSince?: number | null;
  phone?: string | null;
  onEditAddress?: () => void;
  onEditWebsite?: () => void;
  onEditYearsInBusiness?: () => void;
  onEditPhone?: () => void;
};

export const getAgencyProfileBadgeLabels = ({
  address,
  website,
  activeSince,
  phone,
  t,
  emptyWebsiteLabel,
  emptyPhoneLabel,
}: Pick<
  AgencyProfileBadgesProps,
  'address' | 'website' | 'activeSince' | 'phone'
> & {
  t: Translate;
  emptyWebsiteLabel?: string;
  emptyPhoneLabel?: string;
}) => {
  const websiteHostname = website ? extractHostname(website) : null;
  const yearsInBusiness = formatYearsInBusiness(activeSince, t);
  const hasAddress = Boolean(
    address?.route || address?.locality || address?.postcode,
  );

  return {
    address: hasAddress
      ? formatAddress(address ?? {}, ', ', true)
      : t('No address'),
    hasAddress,
    website: websiteHostname || emptyWebsiteLabel || t('Add website'),
    hasWebsite: Boolean(websiteHostname),
    yearsInBusiness,
    phone: getPhoneLabel(phone, emptyPhoneLabel || t('Add phone number')),
    hasPhone: Boolean(phone),
  };
};

export const AgencyProfileBadges = ({
  address,
  website,
  activeSince,
  phone,
  onEditAddress,
  onEditWebsite,
  onEditYearsInBusiness,
  onEditPhone,
}: AgencyProfileBadgesProps) => {
  const { t } = useLocale();

  const labels = useMemo(
    () =>
      getAgencyProfileBadgeLabels({ address, website, activeSince, phone, t }),
    [activeSince, address, phone, t, website],
  );

  return (
    <Stack direction="column" spacing={1.5} sx={{ mt: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <EditableChip
          icon={<MapPin size={14} />}
          label={labels.address}
          onEdit={onEditAddress}
        />

        <EditableChip
          icon={<Globe size={14} />}
          label={labels.website}
          onEdit={onEditWebsite}
        />

        <EditableChip
          icon={<YearsInBusinessIcon />}
          label={labels.yearsInBusiness.label}
          onEdit={onEditYearsInBusiness}
        />
      </Box>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Phone />}
        onClick={onEditPhone}
        sx={{ alignSelf: 'flex-start' }}
      >
        {labels.phone}
      </Button>
    </Stack>
  );
};
